// CantieriCostiView.jsx — amm-v3 (A5, ondata 32): la vista interna "Costi per
// cantiere" (pagina 'cantieri-costi') in React, flag-gated
// (shell_amm_v3_cantieri_costi, default OFF).
//
// Replica 1:1 renderCantieriCosti di admin-module.js r.4277-4333: titolo +
// sottotitolo, bottoni ‹ Cantieri / Esporta CSV / anno ‹ ›, tabella read-only
// (Cantiere, Trasferte €, Veicoli, Polizze, Crediti). VISTA DI SOLA LETTURA:
// nessun modale, nessuna scrittura. Riga 'Senza cantiere' (cantiere_id null)
// grigia e non-cliccabile — parità v1 r.4301-4310.
//
// STATO CONDIVISO col v1: anno su AMM.cantieriCostiAnno (r.4279-4280).
//
// DEVIAZIONI DICHIARATE:
//  D-A5.1 caricamento fallito ⇒ StatoBox errore + Riprova (nessun dato) o
//         banner + dati conservati (dati presenti) — famiglia D-A1.5. Il v1
//         sostituisce la pagina con un box d'errore rosso (perde la tabella).
//  D-A5.6 Esporta CSV doppio-click = 1 richiesta (guardia ref); il v1 ne spara 2.

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import { AM_NEW_TEXTS } from './api.js';
import { CC_TEXTS, emptyMessage, risolviAnno, readCantieriCosti, vaiCantieri, esportaCsv } from './cantieri-costi-api.js';

export function CantieriCostiView({ win, tick }) {
  const A = win.AMM;
  const [anno, setAnno] = React.useState(() => risolviAnno(A));
  const [dati, setDati] = React.useState({ loading: true, error: null, righe: null });
  const [reloadN, setReloadN] = React.useState(0);
  const exportingRef = React.useRef(false); // D-A5.6: export doppio-click = 1 (il v1 ne spara 2)

  React.useEffect(() => {
    let vivo = true;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readCantieriCosti(win, anno).then(
      (righe) => { if (vivo) setDati({ loading: false, error: null, righe }); },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN, anno]);

  const reload = () => setReloadN((n) => n + 1);
  const selezionaAnno = (a) => { A.cantieriCostiAnno = a; setAnno(a); };
  const righe = dati.righe;

  const onExport = async () => {
    if (exportingRef.current) return; // doppio click = 1 sola richiesta (D-A5.6)
    exportingRef.current = true;
    try { await esportaCsv(win, anno); }
    catch (e) { A.toast(CC_TEXTS.exportFallito + (e && e.message ? e.message : e)); }
    finally { exportingRef.current = false; }
  };

  // Riga 'Senza cantiere' (cantiere_id null) grigia in OGNI cella (parità v1:
  // il <tr> ha text-slate-400; qui il colore del td del design system va vinto
  // con uno <span> per cella).
  const grey = (r, node) => <span className={r.cantiere_id == null ? 'text-slate-400' : undefined}>{node}</span>;
  const columns = [
    { key: 'nome', label: CC_TEXTS.colCantiere, render: (r) => grey(r, r.nome || CC_TEXTS.dash) },
    { key: 'totale_voci', label: CC_TEXTS.colTrasferte, align: 'right', render: (r) => grey(r, A.fmtEur2(r.totale_voci)) },
    { key: 'n_veicoli', label: CC_TEXTS.colVeicoli, align: 'right', render: (r) => grey(r, r.n_veicoli || 0) },
    { key: 'n_polizze', label: CC_TEXTS.colPolizze, align: 'right', render: (r) => grey(r, r.n_polizze || 0) },
    { key: 'n_crediti', label: CC_TEXTS.colCrediti, align: 'right', render: (r) => grey(r, r.n_crediti || 0) },
  ];

  return (
    <div data-amm-v3="cantieri-costi">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">{CC_TEXTS.title}</h1>
          <div className="text-xs text-slate-400">{CC_TEXTS.sub}</div>
        </div>
        <div className="flex items-center gap-2">
          <button id="amm-cant-back" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={() => vaiCantieri(win)}>{CC_TEXTS.back}</button>
          <button id="amm-cant-export-csv" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={onExport}>{CC_TEXTS.exportCsv}</button>
          <div className="flex items-center gap-1">
            <button id="amm-cant-prev" type="button" className="px-2 py-1 border rounded text-sm bg-white" onClick={() => selezionaAnno(anno - 1)}>{'‹'}</button>
            <span className="px-2 font-semibold tabular-nums">{anno}</span>
            <button id="amm-cant-next" type="button" className="px-2 py-1 border rounded text-sm bg-white" onClick={() => selezionaAnno(anno + 1)}>{'›'}</button>
          </div>
        </div>
      </div>

      {/* D-A5.1: errore con righe presenti ⇒ banner + Riprova, dati conservati */}
      {dati.error && righe && righe.length ? (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" style={{ background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={reload}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={righe || []}
        rowKey={(r) => String(r.cantiere_id == null ? '__none__' : r.cantiere_id)}
        loading={dati.loading}
        error={righe && righe.length ? null : dati.error}
        onRetry={reload}
        emptyMessage={emptyMessage(anno)}
      />
    </div>
  );
}

// Factory (pattern createAmmV3Esercizi)
export function createAmmV3CantieriCosti(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0 && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1;
    root.render(<CantieriCostiView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
